import { PropGraphDocsArticle } from "./styles";

export function PropGraphOverviewDocument({ activeKey }: { activeKey: string }) {
  return (
    <PropGraphDocsArticle>
      <h2>道具整体设计文档</h2>
      <p>
        炼金道具使用节点图描述运行逻辑。基础字段负责定义道具身份和静态属性，
        节点图负责定义事件、目标、条件、数值和动作之间的关系。
      </p>
      {(activeKey === "overview" || activeKey === "overview-goal") && (
        <>
          <h3>设计目标</h3>
          <ul>
            <li>用节点组合表达道具效果，减少规则硬编码。</li>
            <li>让强度估算、模拟推演和运行时数据都以同一份节点图为准。</li>
            <li>让复杂逻辑拆成稳定的原子节点，节点之间通过明确端口连接。</li>
          </ul>
        </>
      )}
      {(activeKey === "overview" || activeKey === "overview-data") && (
        <>
          <h3>数据边界</h3>
          <table>
            <tbody>
              <tr><th>基础字段</th><td>名称、资源 ID、类型、品质、标签、衍生物、基础产出。</td></tr>
              <tr><th>图结构</th><td>事件节点、目标节点、筛选节点、取值节点、计算节点、条件节点、动作节点，以及端口绑定关系。</td></tr>
              <tr><th>表现字段</th><td>节点坐标、标题和注释只用于组织节点图，不参与游戏运行。</td></tr>
              <tr><th>运行时图</th><td>面向游戏端的精简结构，只保留执行所需的节点和输入关系。</td></tr>
            </tbody>
          </table>
        </>
      )}
      {(activeKey === "overview" || activeKey === "overview-workflow") && (
        <>
          <h3>运行流程</h3>
          <ol>
            <li>读取道具基础信息，确定类型、品质、标签和基础产出。</li>
            <li>通过事件节点建立触发入口。</li>
            <li>通过目标、筛选、取值、计算和条件节点准备动作输入。</li>
            <li>连接动作节点，形成流程、完成、失败、每次等执行分支。</li>
            <li>执行前检查节点端口和必填参数，运行时只读取执行所需数据。</li>
          </ol>
        </>
      )}
    </PropGraphDocsArticle>
  );
}

export function PropGraphNodeDesignDocument({ activeKey }: { activeKey: string }) {
  return (
    <PropGraphDocsArticle>
      <h2>节点设计文档</h2>
      <p>
        节点按职责拆分。事件节点负责触发，目标节点负责取对象，筛选节点负责缩小对象范围，
        取值和计算节点负责生成数值，条件节点负责控制动作是否触发，动作节点负责改变产出、状态或材料库。
      </p>
      {(activeKey === "nodes" || activeKey === "nodes-event") && (
        <>
          <h3>事件节点</h3>
          <table>
            <tbody>
              <tr><th>回合开始</th><td>在回合开始时输出流程，可通过间隔和循环控制触发节奏。</td></tr>
              <tr><th>持有触发</th><td>道具被持有期间持续生效，适合品质权重、光环和常驻修正。</td></tr>
              <tr><th>主动使用</th><td>玩家使用辅料时触发，适合一次性或持续生效的辅料效果。</td></tr>
              <tr><th>消除触发</th><td>指定目标被消除时触发，适合死亡、灰烬、转生等逻辑。</td></tr>
            </tbody>
          </table>
        </>
      )}
      {(activeKey === "nodes" || activeKey === "nodes-target") && (
        <>
          <h3>目标节点</h3>
          <ul>
            <li><code>自身</code> 输出当前道具实例。</li>
            <li><code>盘面原料</code> 从盘面按全部、相对位置或绝对位置输出目标数组。</li>
            <li><code>原型库</code> 输出指定类型的道具原型，用于生成和转化。</li>
            <li><code>原型</code> 输出一个固定道具原型，比较时使用实例的原型 ID。</li>
          </ul>
        </>
      )}
      {(activeKey === "nodes" || activeKey === "nodes-filter") && (
        <>
          <h3>筛选节点</h3>
          <ul>
            <li><code>品质筛选</code> 按一个或多个品质筛选目标。</li>
            <li><code>标签筛选</code> 按一个或多个标签筛选目标。</li>
            <li><code>原型筛选</code> 按道具原型筛选目标，比较实例的原型 ID。</li>
            <li><code>随机筛选</code> 从目标数组中随机保留指定数量。</li>
            <li><code>条件筛选</code> 使用布尔条件过滤目标。</li>
          </ul>
        </>
      )}
      {(activeKey === "nodes" || activeKey === "nodes-value") && (
        <>
          <h3>取值与计算节点</h3>
          <p>
            取值节点输出整数、浮点数、品质或标签。计算节点用于加减乘除、取余、最小、最大和取整，
            适合表达“每有 N 个目标就产生 M 点收益”这类公式。
          </p>
        </>
      )}
      {(activeKey === "nodes" || activeKey === "nodes-condition") && (
        <>
          <h3>条件节点</h3>
          <p>
            条件节点输出布尔值，通常连接到动作的条件端口。多个条件连接到同一动作时，运行时按全部满足处理。
            概率节点用于表达随机触发，比较节点用于表达数量、品质、产出等数值判断。
          </p>
        </>
      )}
      {(activeKey === "nodes" || activeKey === "nodes-action") && (
        <>
          <h3>动作节点</h3>
          <table>
            <tbody>
              <tr><th>成长</th><td>永久改变目标的魔力产出。</td></tr>
              <tr><th>额外产出</th><td>本次反应额外增加或减少魔力，可使用数量和倍率输入。</td></tr>
              <tr><th>永久加成</th><td>让目标此后的产出增加数值或倍率，可通过互斥标签控制同类效果。</td></tr>
              <tr><th>加速</th><td>改变目标倒计时，常用于植物生命周期。</td></tr>
              <tr><th>生成 / 消除 / 转化</th><td>永久改变材料库或盘面实例，需要重点评估正负价值。</td></tr>
              <tr><th>保护 / 净化 / 免疫</th><td>用于防御、负面效果处理和避免被消除。</td></tr>
              <tr><th>收集 / 收获</th><td>辅料延迟收益机制，收集时暂存魔力，收获时释放。</td></tr>
              <tr><th>品质权重 / 延长调和 / 调换位置</th><td>影响抽取质量、委托轮数和位置类道具的触发条件。</td></tr>
            </tbody>
          </table>
        </>
      )}
    </PropGraphDocsArticle>
  );
}
